// Setup › People (`/c/{slug}/setup/people`; design spec §9, coverage matrix rows 9, 10, 13):
// the company's people with search and filters, "Invite" (`POST …/people`), and per person
// "Edit" for name, roles and status (`PATCH …/people/{id}`). The server's rules are mirrored so
// no control is offered that would be refused (services/api/src/routes/org/people.ts):
// - invite `owner` only as an owner, `admin` as an owner or admin, `member` and `client_viewer`
//   as any manager; a new invite is a `member` unless changed (MEMBER-ROLE: the team's people
//   work in Chat and Work; a client viewer reads scorecards and reports only);
// - change an owner only as an owner, an admin only as an owner or admin; an agency operator
//   acting without person authority never changes an owner or admin; granting admin needs an
//   owner or admin, granting owner an owner (the server keeps one active owner, 409);
// - deactivating drops every role and is confirmed first.
// SETUP-REGROUP (review #18): what a person may open is called "Access" on this screen (Owner,
// Admin, Member, Client viewer), never "Role", which is a seat's job role (Setup › Job roles).
// MEMBER-UX: each row's role is a dropdown (Member, Admin, Owner, Client viewer) that sets that one
// role; the last active owner's other options are disabled with a tooltip saying why; Deactivate
// (or Reactivate, back as a Member) shows on row hover. "Invite people" takes several addresses at
// once with one role, Member unless changed; the name comes from the address. "Edit" keeps name,
// status and several roles at once.
// The "Pending" tab lists the invitations not yet accepted (`GET …/invitations`), each with
// "Resend" (a new invitation for the same email replaces the earlier link) and "Revoke"
// (`DELETE …/invitations/{id}`, confirmed first; the mailed link stops working). An invitation
// moves to the People tab once accepted. Revoking follows who may invite the relations.
// Search and filters work on the loaded pages; "Load more" brings the rest (cursor paging).
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  AvatarTile,
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SearchField,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Toolbar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MailIcon, PencilIcon, RotateCwIcon, UserPlusIcon, UsersIcon, XIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyRole, Invitation, Person } from "../../api/types";
import { type CompanyAccess, isOperatorOnly } from "../company";
import { formatDate } from "../format";
import { invitationsQuery, keys, peopleQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { problemText } from "./problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "./row-actions";
import { SetupPage } from "./setup-page";

export const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  client_viewer: "Client viewer",
  agency_operator: "Agency operator",
};
const STATUS_LABELS: Record<Person["status"], string> = {
  active: "Active",
  paused: "Paused",
  deactivated: "Deactivated",
};
const STATUS_TONE: Record<Person["status"], "success" | "warning" | "neutral"> = {
  active: "success",
  paused: "warning",
  deactivated: "neutral",
};
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type InviteRole = "owner" | "admin" | "member" | "client_viewer";
const ROLES: readonly InviteRole[] = ["owner", "admin", "member", "client_viewer"];
/** The role a new invite starts with (MEMBER-ROLE). */
const DEFAULT_INVITE: InviteRole = "member";
type Status = Person["status"];

/** What the caller may do (the server's rules; README conflict 14). */
export function authorityOf(roles: readonly CompanyRole[]) {
  const owner = roles.includes("owner");
  const admin = roles.includes("admin");
  const operatorOnly = roles.includes("agency_operator") && !owner && !admin;
  return {
    owner,
    admin,
    operatorOnly,
    invitable: [
      ...(owner ? (["owner"] as const) : []),
      ...(owner || admin ? (["admin"] as const) : []),
      "member" as const,
      "client_viewer" as const,
    ] as InviteRole[],
    grantsAdmin: owner || admin,
    grantsOwner: owner,
  };
}

/** Null when the caller may change `target`, else why not. */
export function whyNotEditable(roles: readonly CompanyRole[], target: Person): string | null {
  const a = authorityOf(roles);
  if (target.kind !== "person") return "Digital employees are managed under Digital employees.";
  if (a.operatorOnly && (target.roles.includes("owner") || target.roles.includes("admin")))
    return "An agency operator can't change an owner or admin.";
  if (target.roles.includes("owner") && !a.owner) return "Only an owner can change an owner.";
  if (target.roles.includes("admin") && !a.owner && !a.admin)
    return "Only an owner or admin can change an admin.";
  return null;
}

export function PeopleScreen({
  api,
  company,
  invite = false,
}: {
  api: Api;
  company: CompanyAccess;
  /** SEARCH-NOTIF: ⌘K's "Invite person" opens the invite dialog at once (`?invite=1`). */
  invite?: boolean;
}) {
  const q = useInfiniteQuery(peopleQuery(api, company.id));
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | Status>("all");
  const [role, setRole] = useState("all");
  const [inviting, setInviting] = useState(invite);
  const [tab, setTab] = useState<"people" | "pending">("people");
  const [editing, setEditing] = useState<Person | null>(null);
  const [deactivating, setDeactivating] = useState<Person | null>(null);
  const qc = useQueryClient();
  const all = q.data?.pages.flatMap((p) => p.data) ?? [];
  // The last active owner (only when every person is loaded, so the count is the company's).
  const owners = all.filter((p) => p.status === "active" && p.roles.includes("owner"));
  const isLastOwner = (p: Person) =>
    !q.hasNextPage && owners.length === 1 && owners[0]?.id === p.id;
  // Row actions: Deactivate (confirmed first) and Reactivate (back as a Member).
  const status_ = useMutation({
    mutationFn: (v: { person: Person; status: "active" | "deactivated" }) =>
      api.client.people.update({
        params: { co: company.id, id: v.person.id },
        body:
          v.status === "active" ? { status: "active", roles: ["member"] } : { status: v.status },
      }),
    onSuccess: (p, v) => {
      void qc.invalidateQueries({ queryKey: keys.people(company.id) });
      toast.add({
        title:
          v.status === "active"
            ? `${workerName(p.display_name)} reactivated as a Member`
            : `${workerName(p.display_name)} deactivated`,
        type: "success",
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "The change was not made."), type: "error" }),
  });
  const needle = search.trim().toLowerCase();
  const rows = all.filter(
    (p) =>
      (!needle ||
        p.display_name.toLowerCase().includes(needle) ||
        (p.email ?? "").toLowerCase().includes(needle)) &&
      (status === "all" || p.status === status) &&
      (role === "all" ||
        (role === "none" ? p.roles.length === 0 : p.roles.includes(role as CompanyRole))),
  );
  const filtered = needle !== "" || status !== "all" || role !== "all";
  // OPERATOR-ONLY-UX: the agency's operator with no person membership (a company with no
  // owner) may invite plain members and client viewers; that is the way forward here.
  const operatorOnly = isOperatorOnly(company);
  const inviteLabel = operatorOnly ? "Invite a member" : "Invite people";

  return (
    <SetupPage
      title="People"
      {...(q.data ? { count: all.length } : {})}
      actions={
        <Button size="sm" onClick={() => setInviting(true)}>
          <UserPlusIcon data-icon="inline-start" />
          {inviteLabel}
        </Button>
      }
    >
      {operatorOnly ? (
        <Alert data-slot="operator-only-people">
          <UserPlusIcon aria-hidden="true" />
          <AlertTitle>Invite a member to open chat, spaces and task lists</AlertTitle>
          <AlertDescription>
            You manage this company as your agency's operator, with no person membership. Chat, new
            spaces and personal task lists need a person member: invite members here. A member who
            holds the approver seat decides approvals. Owners and admins can't be added by the
            agency yet. Invitations go out by email, so they arrive only once this server's mail
            delivery is set up.
          </AlertDescription>
        </Alert>
      ) : null}
      <InviteDialog
        api={api}
        company={company}
        open={inviting}
        onOpenChange={setInviting}
        onSent={() => setTab("pending")}
      />
      <Toolbar className="flex-wrap max-md:h-auto max-md:py-2">
        <Segmented
          size="sm"
          aria-label="Show"
          value={tab}
          onValueChange={setTab}
          options={[
            { value: "people", label: "People" },
            { value: "pending", label: "Pending" },
          ]}
        />
        {tab === "people" ? (
          <>
            <SearchField
              aria-label="Search people"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Segmented
              size="sm"
              aria-label="Status"
              value={status}
              onValueChange={setStatus}
              options={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "paused", label: "Paused" },
                { value: "deactivated", label: "Deactivated" },
              ]}
            />
            <NativeSelect
              aria-label="Access"
              size="sm"
              className="md:ml-auto"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <NativeSelectOption value="all">Any access</NativeSelectOption>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <NativeSelectOption key={k} value={k}>
                  {v}
                </NativeSelectOption>
              ))}
              <NativeSelectOption value="none">No access</NativeSelectOption>
            </NativeSelect>
          </>
        ) : null}
      </Toolbar>
      {tab === "pending" ? (
        <PendingInvitations api={api} company={company} />
      ) : q.isPending ? (
        <Loading label="Loading people" rows={4} />
      ) : q.isError ? (
        <LoadError what="People" error={q.error} />
      ) : !all.length ? (
        <EmptyState
          variant="card"
          icon={UsersIcon}
          title="No people yet"
          actions={
            <Button size="sm" onClick={() => setInviting(true)}>
              {inviteLabel}
            </Button>
          }
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <Table data-slot="setup-people" className={DENSE_TABLE}>
            <TableCaption className="sr-only">People of the company and their access.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Name</TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Email
                </TableHead>
                <TableHead scope="col">Access</TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col" className="max-lg:hidden">
                  Joined
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    No one matches these filters
                    {q.hasNextPage ? " in the loaded people: load more to search everyone." : "."}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((p) => {
                  const why = whyNotEditable(company.roles, p);
                  const last = isLastOwner(p);
                  return (
                    <TableRow key={p.id} data-person={p.id} className="group/person">
                      <TableHead scope="row" className="font-normal">
                        <span className="flex min-w-0 items-center gap-2.5">
                          <AvatarTile
                            name={workerName(p.display_name)}
                            size="md"
                            aria-hidden="true"
                            className="max-sm:hidden"
                          />
                          {p.kind === "digital_employee" ? (
                            <WorkerBadge kind="de" size="sm" />
                          ) : null}
                          <span className="truncate font-medium">{workerName(p.display_name)}</span>
                        </span>
                      </TableHead>
                      <TableCell
                        className="max-w-[224px] truncate max-md:hidden"
                        title={p.email ?? undefined}
                      >
                        {p.email ?? "—"}
                      </TableCell>
                      <TableCell>
                        <RoleCell
                          api={api}
                          company={company}
                          person={p}
                          why={why}
                          lastOwner={last}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_TONE[p.status]}>{STATUS_LABELS[p.status]}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap max-lg:hidden">
                        {formatDate(p.joined_at)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        <span className="flex items-center justify-end gap-1">
                          {why === null && p.kind === "person" ? (
                            p.status === "deactivated" ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                data-slot="person-reactivate"
                                className={HOVER_ONLY}
                                disabled={status_.isPending}
                                aria-label={`Reactivate ${workerName(p.display_name)}`}
                                onClick={() => status_.mutate({ person: p, status: "active" })}
                              >
                                Reactivate
                              </Button>
                            ) : last ? (
                              <Tooltip>
                                <TooltipTrigger
                                  render={<span className={cn("inline-flex", HOVER_ONLY)} />}
                                >
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled
                                    aria-label={`Deactivate ${workerName(p.display_name)}`}
                                  >
                                    Deactivate
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>{LAST_OWNER}</TooltipContent>
                              </Tooltip>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                data-slot="person-deactivate"
                                className={cn(HOVER_ONLY, "text-danger-text")}
                                aria-label={`Deactivate ${workerName(p.display_name)}`}
                                onClick={() => setDeactivating(p)}
                              >
                                Deactivate
                              </Button>
                            )
                          ) : null}
                          {why === null ? (
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit ${workerName(p.display_name)}`}
                              tip="Name, access and status"
                              className={HOVER_ONLY}
                              onClick={() => setEditing(p)}
                            />
                          ) : (
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              disabled
                              title={why}
                              aria-label={`Edit ${workerName(p.display_name)}`}
                              className={HOVER_ONLY}
                            >
                              <PencilIcon aria-hidden="true" />
                            </Button>
                          )}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {filtered ? (
            <p className="text-xs text-muted-foreground" role="status">
              {rows.length} of {all.length} loaded people shown.
            </p>
          ) : null}
          <LoadMore
            what="people"
            hasMore={q.hasNextPage}
            loading={q.isFetchingNextPage}
            onMore={() => void q.fetchNextPage()}
          />
        </div>
      )}
      {editing ? (
        <EditPersonDialog
          key={editing.id}
          api={api}
          company={company}
          person={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <AlertDialog open={deactivating !== null} onOpenChange={(o) => !o && setDeactivating(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Deactivate {workerName(deactivating?.display_name ?? "")}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They lose access to {company.name} and their access is removed. You can reactivate
              them later; they come back as a Member.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deactivating) status_.mutate({ person: deactivating, status: "deactivated" });
                setDeactivating(null);
              }}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SetupPage>
  );
}

const LAST_OWNER = "The company keeps at least one active owner: make someone else an owner first.";
/** Row actions that show on hover or keyboard focus (always on touch screens). */
const HOVER_ONLY =
  "opacity-0 group-hover/person:opacity-100 group-focus-within/person:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100";
/** The role dropdown's order (MEMBER-UX). */
const ROLE_MENU: readonly InviteRole[] = ["member", "admin", "owner", "client_viewer"];

/** The one role a row shows: the strongest it holds (a person may hold several). */
function primaryRole(roles: readonly CompanyRole[]): InviteRole | null {
  for (const r of ["owner", "admin", "member", "client_viewer"] as const)
    if (roles.includes(r)) return r;
  return null;
}

/**
 * A row's role as a dropdown (MEMBER-UX). Picking one sets exactly that role (`PATCH …/people/
 * {id}` with `roles: [role]`); several roles at once stay possible in "Edit". Options the caller
 * may not give are disabled with the reason; the last active owner's other options are disabled
 * and a tooltip says why (the server answers 409 otherwise).
 */
function RoleCell({
  api,
  company,
  person,
  why,
  lastOwner,
}: {
  api: Api;
  company: CompanyAccess;
  person: Person;
  why: string | null;
  lastOwner: boolean;
}) {
  const qc = useQueryClient();
  const a = authorityOf(company.roles);
  const current = primaryRole(person.roles);
  const extra = person.roles.filter((r) => r !== current && r !== "agency_operator");
  const change = useMutation({
    mutationFn: (role: InviteRole) =>
      api.client.people.update({
        params: { co: company.id, id: person.id },
        body: { roles: [role] },
      }),
    onSuccess: (p, role) => {
      void qc.invalidateQueries({ queryKey: keys.people(company.id) });
      toast.add({
        title: `${workerName(p.display_name)} is now ${ROLE_LABELS[role]}`,
        type: "success",
      });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The access was not changed."), type: "error" }),
  });
  if (person.kind !== "person" || person.status !== "active")
    return <span className="text-muted-foreground">—</span>;
  const reason = (r: InviteRole): string | null =>
    r === current
      ? null
      : lastOwner
        ? "last owner"
        : r === "owner" && !a.grantsOwner
          ? "owners only"
          : r === "admin" && !a.grantsAdmin
            ? "owners and admins only"
            : null;
  const select = (
    <NativeSelect
      size="sm"
      className="w-32"
      aria-label={`Access of ${person.display_name}`}
      data-slot="person-role"
      value={current ?? ""}
      disabled={why !== null || change.isPending}
      title={why ?? undefined}
      onChange={(e) => change.mutate(e.target.value as InviteRole)}
    >
      {current === null ? (
        <NativeSelectOption value="" disabled>
          No access
        </NativeSelectOption>
      ) : null}
      {ROLE_MENU.map((r) => {
        const no = reason(r);
        return (
          <NativeSelectOption
            key={r}
            value={r}
            disabled={no !== null}
            title={no === "last owner" ? LAST_OWNER : (no ?? undefined)}
          >
            {ROLE_LABELS[r]}
            {no ? ` (${no})` : ""}
          </NativeSelectOption>
        );
      })}
    </NativeSelect>
  );
  return (
    <span className="flex items-center gap-1.5">
      {lastOwner ? (
        <Tooltip>
          <TooltipTrigger render={<span className="inline-flex" data-slot="last-owner" />}>
            {select}
          </TooltipTrigger>
          <TooltipContent>{LAST_OWNER}</TooltipContent>
        </Tooltip>
      ) : (
        select
      )}
      {extra.length ? (
        <Badge variant="outline" title={extra.map((r) => ROLE_LABELS[r] ?? r).join(", ")}>
          +{extra.length}
        </Badge>
      ) : null}
    </span>
  );
}

function RoleChecks({
  idPrefix,
  options,
  value,
  onChange,
  disabledReason,
}: {
  idPrefix: string;
  options: InviteRole[];
  value: InviteRole[];
  onChange: (next: InviteRole[]) => void;
  disabledReason?: (r: InviteRole) => string | null;
}) {
  return (
    <fieldset className="flex flex-col gap-2.5">
      <legend className="mb-2 text-xs font-medium text-secondary">Access</legend>
      {options.map((r) => {
        const reason = disabledReason?.(r) ?? null;
        return (
          <Field key={r} orientation="horizontal">
            <Checkbox
              id={`${idPrefix}-${r}`}
              checked={value.includes(r)}
              disabled={reason !== null}
              onCheckedChange={(on) => onChange(on ? [...value, r] : value.filter((x) => x !== r))}
            />
            <FieldLabel htmlFor={`${idPrefix}-${r}`} className="font-normal">
              {ROLE_LABELS[r]}
              {reason ? <span className="ml-1 text-muted-foreground">({reason})</span> : null}
            </FieldLabel>
          </Field>
        );
      })}
    </fieldset>
  );
}

/** Invite people (Setup › People; MODALS: also ⌘K's "Invite person" and Home's checklist). */
export function InviteDialog({
  api,
  company,
  open,
  onOpenChange,
  onSent,
}: {
  api: Api;
  company: CompanyAccess;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: () => void;
}) {
  const qc = useQueryClient();
  const a = authorityOf(company.roles);
  const [text, setText] = useState("");
  const [role, setRole] = useState<InviteRole>(DEFAULT_INVITE);
  const [touched, setTouched] = useState(false);
  const [failed, setFailed] = useState<{ email: string; why: string }[]>([]);
  const emails = parseEmails(text);
  const bad = emails.filter((e) => !EMAIL.test(e));
  // One `people.invite` per address, in order; the ones refused stay in the box with the reason.
  const invite = useMutation({
    mutationFn: async (list: string[]) => {
      const sent: string[] = [];
      const refused: { email: string; why: string }[] = [];
      for (const email of list) {
        try {
          await api.client.people.invite({
            params: { co: company.id },
            body: { email, display_name: nameFromEmail(email), roles: [role] },
          });
          sent.push(email);
        } catch (e) {
          refused.push({ email, why: problemText(e, "The invitation was not sent.") });
        }
      }
      return { sent, refused };
    },
    onSuccess: ({ sent, refused }) => {
      if (sent.length) {
        toast.add({
          title:
            sent.length === 1
              ? `Invitation sent to ${sent[0]}`
              : `${sent.length} invitations sent as ${ROLE_LABELS[role]}`,
          type: "success",
        });
        void qc.invalidateQueries({ queryKey: keys.invitations(company.id) });
        onSent();
      }
      setFailed(refused);
      setText(refused.map((r) => r.email).join("\n"));
      if (refused.length === 0) {
        setRole(DEFAULT_INVITE);
        setTouched(false);
        onOpenChange(false);
      }
    },
  });
  const invalid = emails.length === 0 || bad.length > 0;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!invalid) invite.mutate(emails);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !invite.isPending && onOpenChange(o)}>
      <DialogContent>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Invite people to {company.name}</DialogTitle>
            <DialogDescription>
              Each gets an email with a link and joins with this access once they accept.
            </DialogDescription>
          </DialogHeader>
          <Field data-invalid={(touched && invalid) || undefined}>
            <FieldLabel htmlFor="invite-emails">Emails</FieldLabel>
            <Textarea
              id="invite-emails"
              rows={3}
              placeholder="ana@company.com, ben@company.com"
              value={text}
              aria-invalid={(touched && invalid) || undefined}
              onChange={(e) => {
                setText(e.target.value);
                setFailed([]);
              }}
            />
            <FieldDescription>
              One or more addresses, separated by commas, spaces or new lines.
              {emails.length > 1 ? ` ${emails.length} people.` : ""}
            </FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="invite-role">Access</FieldLabel>
            <NativeSelect
              id="invite-role"
              className="w-full"
              value={role}
              onChange={(e) => setRole(e.target.value as InviteRole)}
            >
              {ROLE_MENU.filter((r) => a.invitable.includes(r)).map((r) => (
                <NativeSelectOption key={r} value={r}>
                  {ROLE_LABELS[r]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <FieldDescription>{ROLE_HINTS[role]}</FieldDescription>
          </Field>
          {touched && invalid ? (
            <p role="alert" className="text-xs text-danger-text">
              {bad.length
                ? `Not an email address: ${bad.join(", ")}`
                : "Enter at least one email address."}
            </p>
          ) : null}
          {failed.length ? (
            <Notice tone="danger" role="alert">
              {failed.map((f) => `${f.email}: ${f.why}`).join(" ")}
            </Notice>
          ) : null}
          <FieldDescription>
            Inviting an address again sends a new link and the old one stops working.
          </FieldDescription>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" />} disabled={invite.isPending}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={invite.isPending}>
              <MailIcon data-icon="inline-start" />
              {invite.isPending
                ? "Sending…"
                : emails.length > 1
                  ? `Send ${emails.length} invitations`
                  : "Send invitation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ROLE_HINTS: Record<InviteRole, string> = {
  member: "Works in Chat and Work, reads the org chart. The usual choice.",
  admin: "Runs the company's setup: people, seats, metrics, add-ons.",
  owner: "Everything an admin does, and can make owners.",
  client_viewer: "Reads scorecards and reports only.",
};

/** The addresses typed into the box: split on commas, semicolons and whitespace, once each. */
export function parseEmails(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const e = raw.trim().replace(/^<|>$/g, "").toLowerCase();
    if (e && !out.includes(e)) out.push(e);
  }
  return out;
}

/** A display name from an address ("ana.lopez@x" → "Ana Lopez"); they can change it later. */
export function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const words = local.split(/[._+-]+/).filter(Boolean);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || local || email;
}

/** The "Pending" tab: invitations not yet accepted, with Resend and Revoke. */
function PendingInvitations({ api, company }: { api: Api; company: CompanyAccess }) {
  const qc = useQueryClient();
  const q = useInfiniteQuery(invitationsQuery(api, company.id));
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.invitations(company.id) });
  const resend = useMutation({
    mutationFn: (i: Invitation) =>
      api.client.people.invite({
        params: { co: company.id },
        body: { email: i.email, display_name: i.display_name, roles: i.roles },
      }),
    onSuccess: (inv) => {
      void refresh();
      toast.add({ title: `New link sent to ${inv.email}`, type: "success" });
    },
  });
  const revoke = useMutation({
    mutationFn: (i: Invitation) =>
      api.client.invitations.revoke({ params: { co: company.id, id: i.id } }),
    onSuccess: (inv) => {
      void refresh();
      toast.add({ title: `Invitation to ${inv.email} revoked`, type: "success" });
    },
  });
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const mayChange = (i: Invitation) => {
    const a = authorityOf(company.roles);
    return i.roles.every((r) => a.invitable.includes(r));
  };
  if (q.isPending) return <Loading label="Loading pending invitations" rows={3} />;
  if (q.isError) return <LoadError what="Pending invitations" error={q.error} />;
  if (!rows.length)
    return <EmptyState variant="card" icon={MailIcon} title="No pending invitations" />;
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {resend.isError || revoke.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(resend.error ?? revoke.error, "The change was not made.")}
        </Notice>
      ) : null}
      <Table data-slot="pending-invitations" className={DENSE_TABLE}>
        <TableCaption className="sr-only">Invitations that have not been accepted.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Name</TableHead>
            <TableHead scope="col" className="max-md:hidden">
              Email
            </TableHead>
            <TableHead scope="col">Access</TableHead>
            <TableHead scope="col" className="max-lg:hidden">
              Expires
            </TableHead>
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((i) => (
            <TableRow key={i.id} data-invitation={i.id} className="group/row">
              <TableHead scope="row" className="font-medium">
                {i.display_name}
              </TableHead>
              <TableCell className="max-md:hidden">{i.email}</TableCell>
              <TableCell>
                <span className="flex flex-wrap gap-1">
                  {i.roles.map((r) => (
                    <Badge key={r} variant="outline">
                      {ROLE_LABELS[r] ?? r}
                    </Badge>
                  ))}
                </span>
              </TableCell>
              <TableCell className="max-lg:hidden">{formatDate(i.expires_at)}</TableCell>
              <TableCell className="text-right">
                <RowActions>
                  <RowAction
                    icon={RotateCwIcon}
                    label={`Resend the invitation to ${i.email}`}
                    tip="Resend"
                    disabled={resend.isPending || !mayChange(i)}
                    onClick={() => resend.mutate(i)}
                  />
                  <RowAction
                    icon={XIcon}
                    label={`Revoke the invitation to ${i.email}`}
                    tip="Revoke"
                    className="hover:text-danger-text"
                    disabled={revoke.isPending || !mayChange(i)}
                    title={
                      mayChange(i) ? undefined : "Only an owner can change an owner invitation."
                    }
                    onClick={() => setRevoking(i)}
                  />
                </RowActions>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <LoadMore
        what="invitations"
        hasMore={q.hasNextPage}
        loading={q.isFetchingNextPage}
        onMore={() => void q.fetchNextPage()}
      />
      <AlertDialog open={revoking !== null} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke the invitation to {revoking?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              The link in their email stops working. You can invite them again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (revoking) revoke.mutate(revoking);
                setRevoking(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditPersonDialog({
  api,
  company,
  person,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  person: Person;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const a = authorityOf(company.roles);
  const managed = (r: CompanyRole): r is InviteRole => (ROLES as readonly string[]).includes(r);
  const [name, setName] = useState(person.display_name);
  const [status, setStatus] = useState<Status>(person.status);
  const [roles, setRoles] = useState<InviteRole[]>(person.roles.filter(managed));
  const [confirming, setConfirming] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const before = person.roles.filter(managed).sort().join();
      const after = [...roles].sort().join();
      return api.client.people.update({
        params: { co: company.id, id: person.id },
        body: {
          ...(name.trim() !== person.display_name ? { display_name: name.trim() } : {}),
          ...(status !== person.status ? { status } : {}),
          // Roles are set on an active person only; deactivation drops them all.
          ...(status === "active" && after !== before ? { roles } : {}),
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.people(company.id) });
      toast.add({ title: `${name.trim()} saved`, type: "success" });
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (status === "deactivated" && person.status !== "deactivated") setConfirming(true);
    else save.mutate();
  };
  const owner = person.roles.includes("owner");
  return (
    <>
      <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
        <DialogContent>
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
            <DialogHeader>
              <DialogTitle>Edit {person.display_name}</DialogTitle>
              <DialogDescription>{person.email ?? "No email on file"}</DialogDescription>
            </DialogHeader>
            <Field data-invalid={!name.trim() || undefined}>
              <FieldLabel htmlFor="person-name">Name</FieldLabel>
              <Input
                id="person-name"
                className="h-10"
                value={name}
                aria-invalid={!name.trim() || undefined}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="person-status">Status</FieldLabel>
              <NativeSelect
                id="person-status"
                className="w-full"
                value={status}
                onChange={(e) => setStatus(e.target.value as Status)}
              >
                <NativeSelectOption value="active">Active</NativeSelectOption>
                <NativeSelectOption value="paused">
                  Paused (no access until resumed)
                </NativeSelectOption>
                <NativeSelectOption value="deactivated">
                  Deactivated (all access removed)
                </NativeSelectOption>
              </NativeSelect>
            </Field>
            {status === "active" ? (
              <div className="flex flex-col gap-2">
                {owner ? (
                  <p className="text-xs text-muted-foreground">
                    The company keeps at least one active owner.
                  </p>
                ) : null}
                <RoleChecks
                  idPrefix="person-role"
                  options={[...ROLES]}
                  value={roles}
                  onChange={setRoles}
                  disabledReason={(r) =>
                    r === "owner" && !a.grantsOwner
                      ? "owners only"
                      : r === "admin" && !a.grantsAdmin
                        ? "owners and admins only"
                        : null
                  }
                />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Access applies to active people only.</p>
            )}
            {save.isError ? (
              <Notice tone="danger" role="alert">
                {problemText(save.error, "The change was not saved.")}
              </Notice>
            ) : null}
            <DialogFooter>
              <DialogClose render={<Button variant="secondary" />} disabled={save.isPending}>
                Cancel
              </DialogClose>
              <Button
                type="submit"
                variant={
                  status === "deactivated" && person.status !== "deactivated"
                    ? "destructive"
                    : "default"
                }
                disabled={save.isPending || !name.trim()}
              >
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {person.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They lose access to {company.name} and their access is removed. You can reactivate
              them later and give it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setConfirming(false);
                save.mutate();
              }}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
